import { useParams, Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { useCustomNIP, extractNIPTitle } from '@/hooks/useCustomNIPsList';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MY_PUBKEY = 'a8d1560d6a647d501699167246f237b36fb123f89168fda11dc743533fec7a08';

const CustomNIPPage = () => {
  const { dTag } = useParams<{ dTag: string }>();
  const { data: nip, isLoading } = useCustomNIP(MY_PUBKEY, dTag);

  const title = nip ? extractNIPTitle(nip) : null;

  useSeoMeta({
    title: title || `NIP ${dTag}`,
    description: 'Custom Nostr Implementation Possibility by stl1988',
    ogTitle: title || `NIP ${dTag}`,
    ogDescription: 'Custom Nostr Implementation Possibility by stl1988',
    ogImage: 'https://stl1988.shakespeare.wtf/og-image.jpg',
    ogType: 'article',
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container py-8 px-4 max-w-4xl">
          <Button asChild variant="ghost" size="sm" className="mb-6">
            <Link to="/">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Link>
          </Button>

          <Card>
            <CardHeader>
              <Skeleton className="h-8 w-3/4" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!nip) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container py-8 px-4 max-w-4xl">
          <Button asChild variant="ghost" size="sm" className="mb-6">
            <Link to="/">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Link>
          </Button>

          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">NIP not found</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="container py-8 px-4 max-w-4xl">
        <Button asChild variant="ghost" size="sm" className="mb-6">
          <Link to="/">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Link>
        </Button>

        <Card>
          <CardHeader>
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-2xl sm:text-3xl font-heading">
                  {title || dTag}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-2">
                  Custom Nostr Implementation Possibility
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <div className="prose prose-sm sm:prose-base dark:prose-invert max-w-none prose-headings:font-heading prose-a:text-primary hover:prose-a:underline prose-pre:bg-muted prose-pre:border prose-code:text-primary">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {nip.content}
              </ReactMarkdown>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomNIPPage;
